// Shared blog post data, used by the blog index grid and the post detail route.

/**
 * A run of inline text with optional emphasis or a link. Used when block text
 * carries formatting (e.g. parsed from HubSpot). A run can be both bold and
 * italic, and a link can also be emphasized.
 */
export type InlineRun = {
  text: string
  bold?: boolean
  italic?: boolean
  /** When set, the run renders as an anchor to this href. */
  href?: string
}

/**
 * Block text: either a plain string (hand-authored posts) or an array of inline
 * runs (bold/italic/links). The renderer accepts both.
 */
export type Inline = string | InlineRun[]

/**
 * A single piece of article body content. The post detail route renders an
 * ordered list of these blocks (see BlogPostBody). Add new block kinds here and
 * give them a branch in the renderer — keep the two in sync.
 */
export type ContentBlock =
  | { type: 'heading'; text: Inline }
  | { type: 'paragraph'; text: Inline }
  | { type: 'quote'; text: Inline; attribution?: string }
  | { type: 'list'; ordered?: boolean; items: Inline[] }
  | { type: 'image'; src: string; alt: string; caption?: string }
  /** `header` is the column-label row, when the source table marks one. */
  | { type: 'table'; header?: Inline[]; rows: Inline[][]; caption?: string }
  | { type: 'divider' }

/**
 * A post author. `name` is required; richer fields are optional and populated
 * when available (e.g. from HubSpot). Add new fields here as the author byline
 * grows — author photos (`image`), role, profile links, etc.
 */
export type Author = {
  name: string
  /**
   * Role / job title shown after the name in the article byline (e.g. "Designer
   * at Ent"). HubSpot's author record has no title field, so this is parsed from
   * an in-post byline line (`By Name | Title`) at fetch time — see
   * scripts/fetch-blog-posts.ts. Optional: absent when no byline is provided.
   */
  title?: string
  /**
   * Avatar image: a `/images/blog/` filename or an absolute URL, resolved the
   * same way as the hero `image`. Optional until we start surfacing author
   * photos.
   */
  image?: string
}

export type BlogPost = {
  slug: string
  title: string
  /** One or more authors. Render with formatAuthors() for the display byline. */
  authors: Author[]
  image: string
  /** Publish date, ISO 8601 (YYYY-MM-DD). Optional for not-yet-written posts. */
  date?: string
  /** Estimated read time in minutes. Optional. */
  readMinutes?: number
  /** One-sentence standfirst shown under the title and used for previews/SEO. */
  excerpt?: string
  /**
   * HubSpot's dedicated SEO fields, preferred for page <title> / <meta
   * description> over the derived title/excerpt when an editor has set them.
   */
  seoTitle?: string
  metaDescription?: string
  /** ISO date (YYYY-MM-DD) of the last edit — used for sitemap <lastmod> and
   *  article:modified_time. Falls back to `date` when absent. */
  updated?: string
  /**
   * Set from the HubSpot "unlisted" tag at fetch time. An unlisted post keeps
   * its own page and sitemap entry but is left out of every post listing —
   * use LISTED_BLOG_POSTS anywhere posts are listed. The blog index's "Show
   * all posts" button appends them on demand. Not related to
   * isUnlistedRoute (seo.ts), which makes a route noindex and drops it from
   * the sitemap.
   */
  unlisted?: boolean
  /**
   * The article body. When present the detail route renders it; when omitted it
   * falls back to a "coming soon" placeholder. `image` filenames in image blocks
   * are resolved against `/images/blog/` just like the hero `image`.
   */
  content?: ContentBlock[]
}

// The post list is generated at build time by scripts/fetch-blog-posts.ts: it
// pulls published posts from HubSpot, sanitizes their HTML, and converts it to
// the ContentBlock[] this UI renders. The committed JSON is the fallback used
// when HUBSPOT_API_TOKEN is absent (local dev / fork PRs) so the blog always
// has content; with a token, the `fetch:blog` prebuild step overwrites it with
// live content before the bundle is built. Hero/inline image strings may be
// either local /images/blog/ filenames (curated fallback) or absolute HubSpot
// CDN URLs (live content) — see ASSET() in the blog routes.
import generated from './blogPosts.generated.json'

/** Every post, unlisted ones included — for permalinks, prerender and the sitemap. */
export const BLOG_POSTS: BlogPost[] = generated as unknown as BlogPost[]

/** Posts shown in listings (blog index, related posts, llms.txt): unlisted ones skipped. */
export const LISTED_BLOG_POSTS: BlogPost[] = BLOG_POSTS.filter((p) => !p.unlisted)

/** Unlisted posts, revealed by the blog index's "Show all posts" button. */
export const UNLISTED_BLOG_POSTS: BlogPost[] = BLOG_POSTS.filter((p) => p.unlisted)

export const getBlogPost = (slug: string) => BLOG_POSTS.find((p) => p.slug === slug)

/**
 * Split a byline string into individual author names. Names may be separated by
 * commas, ampersands, or the word "and" (e.g. "Ada Lovelace, Alan Turing & Grace
 * Hopper"). Used to parse hand-authored bylines and HubSpot's single authorName
 * field, which editors sometimes fill with multiple co-authors.
 */
export function parseAuthorNames(raw: string): string[] {
  return raw
    .split(/\s*(?:,|&|\band\b)\s*/gi)
    .map((name) => name.trim())
    .filter(Boolean)
}

/**
 * Parse an in-post byline body into Author records. Co-authors are separated by
 * commas; within each, the first "|" splits the name from an optional title.
 * Titles routinely contain "&" / "and" ("co-Founder & CTO"), so the comma — not
 * "&" — is the author separator:
 *
 *   "Matt Weitkamp | Creative Director at Ent"                  → [{ name, title }]
 *   "Brandon Dixon | co-Founder & CTO at Ent"                   → [{ name, title }]  ("&" stays in the title)
 *   "Elias Manousos | CEO at Ent , Brandon Dixon | CTO at Ent"  → [{…}, {…}]
 *
 * The leading "By " and the HTML it lives in are handled by the fetch script's
 * extractByline; this is the pure text-to-authors step. Returns [] for input
 * with no usable names.
 */
export function parseByline(raw: string): Author[] {
  return raw
    .split(',')
    .map((seg) => seg.trim())
    .filter(Boolean)
    .map((seg): Author => {
      const bar = seg.indexOf('|')
      if (bar === -1) return { name: seg.trim() }
      const name = seg.slice(0, bar).trim()
      const title = seg.slice(bar + 1).trim()
      return title ? { name, title } : { name }
    })
    .filter((a) => a.name)
}

/** Longest first line still treated as a byline rather than prose. */
const BYLINE_MAX_LENGTH = 200

/**
 * Decide whether the first line of a post body is an author byline and, if so,
 * parse it. Authors write the byline on the first line as "Name | Title" — the
 * pipe splits name from title, co-authors are separated by commas, and a
 * leading "By " is optional:
 *
 *   "Janani Nagarajan | VP of Product Marketing at Ent"
 *   "Elias Manousos | CEO at Ent , Brandon Dixon | CTO at Ent"
 *
 * The pipe is the marker that tells a byline apart from a normal opening
 * paragraph (so a prose line like "By 2025, security had changed." is NOT
 * treated as one), and a length cap guards against a stray long line that
 * happens to contain "|". Returns null when the line isn't a byline.
 */
export function parseBylineLine(text: string): Author[] | null {
  const t = text.trim()
  if (!t.includes('|') || t.length > BYLINE_MAX_LENGTH) return null
  const authors = parseByline(t.replace(/^By\s+/i, ''))
  return authors.length ? authors : null
}

/** Display byline: "A", "A & B", or "A, B & C". Names only — no titles. */
export function formatAuthors(authors: Author[]): string {
  const names = authors.map((a) => a.name).filter(Boolean)
  if (names.length <= 1) return names[0] ?? ''
  return `${names.slice(0, -1).join(', ')} & ${names[names.length - 1]}`
}

/**
 * Article byline. A title only reads cleanly for a single author ("Name,
 * Title") — with co-authors the titles themselves carry "&"/"," that would
 * collide with any inline separator, so multi-author bylines fall back to the
 * names-only "A, B & C" style (formatAuthors).
 */
export function formatByline(authors: Author[]): string {
  if (authors.length === 1 && authors[0].title) {
    return `${authors[0].name}, ${authors[0].title}`
  }
  return formatAuthors(authors)
}
